import type {
  MonthlyAttendanceReportFilters,
  MonthlyAttendanceReportRow,
} from '../types/monthlyAttendanceReport';
import type { ReportPreviewRow } from '../types/report';

const EMPTY = '—';
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const EMPLOYEE_KEYS = [
  'employeeName',
  'employee_name',
  'employeeFullName',
  'displayName',
  'display_name',
  'fullName',
  'name',
  'employee',
];
const DEPARTMENT_KEYS = [
  'departmentName',
  'department_name',
  'department',
  'departmentId',
];
const PRESENT_KEYS = [
  'presentDays',
  'present_days',
  'presentDayCount',
  'daysPresent',
  'totalPresentDays',
  'present',
];
const ABSENT_KEYS = [
  'absentDays',
  'absent_days',
  'absentDayCount',
  'daysAbsent',
  'totalAbsentDays',
  'absent',
];
const LEAVE_KEYS = [
  'leaveDays',
  'leave_days',
  'onLeaveDays',
  'leaveDayCount',
  'daysOnLeave',
  'totalLeaveDays',
  'leave',
];
const WORKING_KEYS = [
  'workingDays',
  'working_days',
  'workingDayCount',
  'totalWorkingDays',
  'scheduledDays',
  'workDays',
];
const PERCENT_KEYS = [
  'attendancePercentage',
  'attendance_percentage',
  'attendancePercent',
  'percentage',
  'percent',
  'attendancePct',
];

function firstValue(
  values: Record<string, unknown>,
  keys: string[],
): unknown {
  for (const key of keys) {
    if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
      return values[key];
    }
  }
  return null;
}

function asDisplayText(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || EMPTY;
  }
  return EMPTY;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const cleaned = value.trim().replace(/%$/, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asNonNegativeDays(value: unknown): number {
  const parsed = asNumber(value);
  if (parsed === null) return 0;
  return Math.max(0, Math.round(parsed * 100) / 100);
}

export function createDefaultMonthlyAttendanceFilters(
  now = new Date(),
): MonthlyAttendanceReportFilters {
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    department: '',
  };
}

export function formatMonthlyAttendanceLabel(
  year: number,
  month: number,
): string {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return `${month}/${year}`;
  }
  return `${MONTH_LABELS[month - 1]} ${year}`;
}

/** Shorter label for the month nav bar (e.g. "May 2024"). */
export function formatMonthlyAttendanceShortLabel(
  year: number,
  month: number,
): string {
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return `${month}/${year}`;
  }
  const short = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${short[month - 1]} ${year}`;
}

export function validateMonthlyAttendancePeriod(
  year: number,
  month: number,
): string | null {
  if (!Number.isInteger(year) || year < 1000 || year > 9999) {
    return 'Year must be a four-digit year.';
  }
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return 'Month must be between 1 and 12.';
  }
  return null;
}

export function shiftMonthlyAttendancePeriod(
  year: number,
  month: number,
  deltaMonths: number,
): { year: number; month: number } {
  const absolute = year * 12 + (month - 1) + deltaMonths;
  const nextYear = Math.floor(absolute / 12);
  const nextMonth = (absolute % 12) + 1;
  return { year: nextYear, month: nextMonth };
}

/** Preview filter state — year/month as strings for SafeReportFilterState. */
export function monthlyAttendanceApiFilters(
  year: number,
  month: number,
): { year: string; month: string } | null {
  if (validateMonthlyAttendancePeriod(year, month)) return null;
  return { year: String(year), month: String(month) };
}

function resolvePercentage(
  values: Record<string, unknown>,
  presentDays: number,
  workingDays: number | null,
): number | null {
  const fromApi = asNumber(firstValue(values, PERCENT_KEYS));
  if (fromApi !== null) {
    const normalized = fromApi > 0 && fromApi <= 1 ? fromApi * 100 : fromApi;
    return Math.max(0, Math.min(100, Math.round(normalized)));
  }
  if (workingDays !== null && workingDays > 0) {
    return Math.max(
      0,
      Math.min(100, Math.round((presentDays / workingDays) * 100)),
    );
  }
  return null;
}

export function mapMonthlyAttendanceReportRows(
  rows: ReportPreviewRow[],
): MonthlyAttendanceReportRow[] {
  return rows.map((row, index) => {
    const values = row.values;
    // The Employee column shows the name only, never the employee number.
    const employee = asDisplayText(firstValue(values, EMPLOYEE_KEYS));

    const presentDays = asNonNegativeDays(firstValue(values, PRESENT_KEYS));
    const absentDays = asNonNegativeDays(firstValue(values, ABSENT_KEYS));
    const leaveDays = asNonNegativeDays(firstValue(values, LEAVE_KEYS));
    const workingRaw = asNumber(firstValue(values, WORKING_KEYS));
    const workingDays =
      workingRaw === null ? null : Math.max(0, Math.round(workingRaw));
    const departmentValue = firstValue(values, DEPARTMENT_KEYS);

    return {
      rowKey: row.rowKey || `monthly-attendance-${index}`,
      employee,
      presentDays,
      absentDays,
      leaveDays,
      workingDays,
      percentage: resolvePercentage(values, presentDays, workingDays),
      department:
        departmentValue === null ? null : asDisplayText(departmentValue),
    };
  });
}

export function filterMonthlyAttendanceReportRows(
  rows: MonthlyAttendanceReportRow[],
  filters: Pick<MonthlyAttendanceReportFilters, 'department'>,
): MonthlyAttendanceReportRow[] {
  const department = filters.department.trim().toLowerCase();
  const departmentIsId = /^\d+$/.test(department);

  return rows.filter(row => {
    if (department && !departmentIsId && row.department !== null) {
      if (!row.department.toLowerCase().includes(department)) return false;
    }
    return true;
  });
}

export function summarizeMonthlyAttendanceReportRows(
  rows: MonthlyAttendanceReportRow[],
): {
  workingDays: number;
  presentDays: number;
  leaveDays: number;
  absentDays: number;
} {
  if (rows.length === 0) {
    return { workingDays: 0, presentDays: 0, leaveDays: 0, absentDays: 0 };
  }

  let workingDays = 0;
  let presentSum = 0;
  let leaveSum = 0;
  let absentSum = 0;

  for (const row of rows) {
    if (row.workingDays !== null && row.workingDays > workingDays) {
      workingDays = row.workingDays;
    }
    presentSum += row.presentDays;
    leaveSum += row.leaveDays;
    absentSum += row.absentDays;
  }

  if (workingDays === 0) {
    const inferred = rows
      .map(row => row.presentDays + row.absentDays + row.leaveDays)
      .reduce((max, value) => Math.max(max, value), 0);
    workingDays = inferred;
  }

  // Averages keep one decimal so a few leave or absent days across many
  // employees (or half/quarter-day leave) do not round down to 0.
  const count = rows.length;
  const average = (sum: number) => Math.round((sum / count) * 10) / 10;
  return {
    workingDays,
    presentDays: average(presentSum),
    leaveDays: average(leaveSum),
    absentDays: average(absentSum),
  };
}

export function formatAttendancePercentage(
  percentage: number | null,
): string {
  if (percentage === null) return EMPTY;
  return `${percentage}%`;
}
